import QRCode from 'qrcode';
import dayjs from 'dayjs';

// 生成二维码
export const generateQRCode = async (text: string): Promise<string> => {
  try {
    const qrCodeDataURL = await QRCode.toDataURL(text, {
      width: 256,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
    });
    return qrCodeDataURL;
  } catch (error) {
    console.error('生成二维码失败:', error);
    throw error;
  }
};

// 格式化金额（添加千分位分隔符）
export const formatAmount = (amount: string | number): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '0.00';
  return num.toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// 金额元转分
export const amountToFen = (amount: string | number): number => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return Math.round(num * 100);
};

// 格式化日期时间
export const formatDateTime = (dateTime: string | Date, format: string = 'YYYY-MM-DD HH:mm:ss'): string => {
  return dayjs(dateTime).format(format);
};

// 格式化日期
export const formatDate = (date: string | Date): string => {
  return dayjs(date).format('YYYY-MM-DD');
};

// 获取当前时间戳
export const getCurrentTimestamp = (): string => {
  return dayjs().format('YYYYMMDDHHmmss');
};

// 生成随机字符串
export const generateRandomString = (length: number = 8): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// 验证身份证号
export const validateIdCard = (idCard: string): boolean => {
  const regex = /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/;
  return regex.test(idCard);
};

// 验证手机号
export const validateMobile = (mobile: string): boolean => {
  const regex = /^1[3-9]\d{9}$/;
  return regex.test(mobile);
};

// 验证邮箱
export const validateEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

// 验证银行卡号
export const validateBankCard = (cardNo: string): boolean => {
  // 简单的银行卡号验证（16-19位数字）
  const regex = /^\d{16,19}$/;
  return regex.test(cardNo);
};

// 验证统一社会信用代码
export const validateCreditCode = (code: string): boolean => {
  const regex = /^[0-9A-HJ-NPQRTUWXY]{2}\d{6}[0-9A-HJ-NPQRTUWXY]{10}$/;
  return regex.test(code);
};

// 文件大小格式化
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// 防抖函数
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(null, args), wait);
  };
};

// 节流函数
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func.apply(null, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// 深拷贝
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as any;
  if (obj instanceof Array) return obj.map(item => deepClone(item)) as any;
  if (typeof obj === 'object') {
    const clonedObj = {} as any;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
  return obj;
};

// 本地存储工具
export const storage = {
  set: (key: string, value: any): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('存储数据失败:', error);
    }
  },
  
  get: <T>(key: string, defaultValue?: T): T | null => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue || null;
    } catch (error) {
      console.error('获取存储数据失败:', error);
      return defaultValue || null;
    }
  },
  
  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('删除存储数据失败:', error);
    }
  },
  
  clear: (): void => {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('清空存储数据失败:', error);
    }
  },
};

 // 交易类型映射（对账单查询展示的8类）
export const transactionTypeMap: Record<string, string> = {
  '01': '充值',
  '21': '提现',
  '03': '付款',
  '04': '收款',
  '10': '退款',
  '31': '分润付款',
  '32': '分润收款',
  '33': '分润退款',
  // 其他类型如存在数据也可继续补充映射，但账单查询默认只展示上述八类文案
};

// 客户状态映射
export const customerStatusMap: Record<string, { text: string; color: string }> = {
  '0': { text: '待生效', color: 'orange' },
  '1': { text: '生效', color: 'green' },
  '2': { text: '注销', color: 'red' },
};

// 核验状态映射
export const verificationStatusMap: Record<string, { text: string; color: string }> = {
  '0': { text: '待系统核验', color: 'orange' },
  '1': { text: '核验成功', color: 'green' },
  '2': { text: '核验失败', color: 'red' },
  '3': { text: '系统核验中', color: 'blue' },
  '4': { text: '银行审核中', color: 'blue' },
};

// 授权状态映射
export const authStatusMap: Record<string, { text: string; color: string }> = {
  '0': { text: '待用户授权', color: 'orange' },
  '1': { text: '银行审核中', color: 'blue' },
  '2': { text: '授权失败（用户拒绝）', color: 'red' },
  '3': { text: '授权失败（银行拒绝）', color: 'red' },
  '4': { text: '授权成功', color: 'green' },
};